import { base64, escapeHtmlText, imageTypeOf, mapCaption, mapsShowing, type ExportData, type ExportEntry, type ExportMap, type ExportStory } from '@realmery/realm-core';
import { MAP_CSS, mapFigure, pictureUri, placesList, type MapHrefs } from './mapFigure.ts';
import { relinkMentions } from './markdown.ts';
import { entryIdOfLink, sanitizeEntryHtmlToFragment } from '../security/sanitize.ts';

/**
 * The HTML export (docs/PARITY-STEP-1-PLAN.md section 4): the whole realm as one page, as
 * the desktop's `export.HtmlDocumentBuilder` lays it out — a title, the contents, the
 * collections, then a section per entry with its fields, text, relationships and
 * pictures. Differences, for safety, from what the desktop writes:
 * - entry text goes through the entry view's sanitizer, so nothing in it can run;
 * - the page carries its own Content Security Policy: no scripts, no loading from
 *   anywhere, pictures only from the file itself;
 * - a picture is embedded only if its bytes are an image, with the type they show;
 * - mention links point at their entry's section instead of `realmery:entry/<id>`.
 * The Atlas's maps follow the collections, each with its overlay and places (parity step
 * 4), and Story Studio's projects follow the entries, in reading order.
 */

export const EXPORT_POLICY = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'";

const STYLE = `body { font-family: Georgia, 'Times New Roman', serif; max-width: 860px; margin: 2rem auto; padding: 0 1.5rem; line-height: 1.5; color: #222; }
h1 { border-bottom: 3px solid #6b4f2a; padding-bottom: 0.4rem; }
h2 { color: #6b4f2a; margin-top: 2.5rem; }
.entry-type { font-style: italic; color: #666; margin-top: -0.5rem; }
table.metadata { border-collapse: collapse; margin: 0.75rem 0; }
table.metadata th, table.metadata td { border: 1px solid #ccc; padding: 0.3rem 0.6rem; text-align: left; }
.gallery img { max-width: 220px; margin: 0.5rem; border: 1px solid #ccc; }
ul.toc { columns: 2; }
.story h3 { color: #6b4f2a; margin-top: 2rem; }
.story h4 { font-style: italic; }
`;

const anchorId = (entryId: string) => `entry-${entryId}`;

export function htmlExport(data: ExportData): string {
  // The body is built as DOM nodes; the head is written as text, since even a detached
  // document's <style> element counts against the app's own style policy.
  const doc = document.implementation.createHTMLDocument('');
  const head = [
    '<meta charset="UTF-8">',
    `<meta http-equiv="Content-Security-Policy" content="${EXPORT_POLICY}">`,
    '<meta name="referrer" content="no-referrer">',
    `<title>${escapeHtmlText(`${data.realm.name} - Realmery Export`)}</title>`,
    `<style>\n${STYLE}${MAP_CSS}</style>`,
  ].join('\n');
  buildExport(doc, doc.body, data);
  return `<!DOCTYPE html>\n<html lang="en">\n<head>\n${head}\n</head>\n${doc.body.outerHTML}\n</html>\n`;
}

/** How the export is laid out: the HTML file's, or the PDF's (`HtmlDocumentBuilder.build`'s arguments). */
export interface ExportLayout {
  /** Entry pictures, left out of the PDF as the desktop leaves them out. */
  readonly images: boolean;
  /** A map's picture: the original with an overlay, or (for the PDF) one with everything drawn in. */
  readonly mapPicture: (map: ExportMap) => string | null;
  readonly overlay: boolean;
}

const HTML_LAYOUT: ExportLayout = { images: true, mapPicture: (map) => pictureUri(map.map.imageData), overlay: true };

/** The export's content, built into `body` (`HtmlDocumentBuilder.build`). */
export function buildExport(doc: Document, body: HTMLElement, data: ExportData, layout: ExportLayout = HTML_LAYOUT): void {
  const el = <K extends keyof HTMLElementTagNameMap>(parent: Element, tag: K, text?: string, className?: string): HTMLElementTagNameMap[K] => {
    const child = doc.createElement(tag);
    if (text !== undefined) child.textContent = text;
    if (className) child.className = className;
    parent.append(child);
    return child;
  };

  el(body, 'h1', data.realm.name);
  if (data.realm.description.trim() !== '') el(body, 'p', data.realm.description, 'realm-description');

  el(body, 'h2', 'Contents');
  if (data.atlas.maps.length > 0) {
    const maps = el(body, 'ul', undefined, 'toc');
    for (const map of data.atlas.maps) el(el(maps, 'li'), 'a', `🗺 ${map.map.name}`).href = `#${mapAnchor(map)}`;
  }
  if (data.stories.length > 0) {
    const stories = el(body, 'ul', undefined, 'toc');
    for (const story of data.stories) el(el(stories, 'li'), 'a', `📖 ${story.name}`).href = `#${storyAnchor(story)}`;
  }
  const toc = el(body, 'ul', undefined, 'toc');
  for (const entry of data.entries) {
    const link = el(el(toc, 'li'), 'a', `${entry.title} (${entry.typeName})`);
    link.href = `#${anchorId(entry.id)}`;
  }

  if (data.collections.length > 0) {
    el(body, 'h2', 'Collections');
    for (const collection of data.collections) {
      const section = el(body, 'div', undefined, 'collection');
      el(section, 'h3', collection.name);
      const list = el(section, 'ul');
      for (const member of collection.members) el(el(list, 'li'), 'a', member.title).href = `#${anchorId(member.id)}`;
    }
  }

  const ids = new Set(data.entries.map((entry) => entry.id));
  // `appendMaps`: the owner's view, as every export the web makes is.
  if (data.atlas.maps.length > 0) {
    el(body, 'h2', 'Maps');
    const hrefs: MapHrefs = {
      entry: (entryId) => (ids.has(entryId) ? `#${anchorId(entryId)}` : null),
      map: (mapId) => {
        const target = data.atlas.maps.find((m) => m.map.id === mapId);
        return target ? `#${mapAnchor(target)}` : null;
      },
    };
    for (const map of data.atlas.maps) {
      const section = el(body, 'section', undefined, 'map');
      section.id = mapAnchor(map);
      el(section, 'h3', map.map.name);
      if (layout.overlay) {
        section.append(mapFigure(doc, data.atlas, map, hrefs, true, layout.mapPicture(map)));
      } else {
        // The picture already has everything drawn in.
        const figure = el(section, 'div', undefined, 'map-figure');
        const img = el(figure, 'img', undefined, 'map-flat');
        const src = layout.mapPicture(map);
        if (src) img.setAttribute('src', src);
        img.setAttribute('alt', map.map.name);
        const caption = mapCaption(map);
        if (caption !== '') el(figure, 'p', caption, 'map-caption');
      }
      el(section, 'h4', 'Places');
      section.append(placesList(doc, data.atlas, map, hrefs, true));
    }
  }
  for (const entry of data.entries) appendEntry(doc, body, entry, ids, el, data, layout.images);
  for (const story of data.stories) appendStory(doc, body, story, ids, el);
}

const storyAnchor = (story: ExportStory) => `story-${story.slug}`;
const mapAnchor = (map: ExportMap) => `map-${map.slug}`;

/** `appendStories`: an act or chapter heading whenever it changes, then each scene; linked names point at their entry's section. */
function appendStory(doc: Document, body: HTMLElement, story: ExportStory, ids: ReadonlySet<string>, el: Make): void {
  const section = el(body, 'section', undefined, 'story');
  section.id = storyAnchor(story);
  el(section, 'h2', `📖 ${story.name}`);
  if (story.description.trim() !== '') el(section, 'p', story.description, 'story-description');
  el(section, 'p', `${story.words.toLocaleString('en-US')} words`, 'entry-type');
  let act: string | null = null;
  let chapter: string | null = null;
  for (const scene of story.scenes) {
    if (scene.actTitle !== act) {
      act = scene.actTitle;
      chapter = null;
      el(section, 'h3', act, 'story-act');
    }
    if (scene.chapterTitle !== chapter) {
      chapter = scene.chapterTitle;
      el(section, 'h4', chapter, 'story-chapter');
    }
    const block = el(section, 'div', undefined, 'scene');
    el(block, 'h5', scene.title);
    const fragment = doc.importNode(sanitizeEntryHtmlToFragment(scene.contentHtml), true);
    relinkMentions(fragment, (entryId) => (ids.has(entryId) ? `#${anchorId(entryId)}` : null));
    el(block, 'div', undefined, 'content').append(fragment);
  }
}

type Make = <K extends keyof HTMLElementTagNameMap>(parent: Element, tag: K, text?: string, className?: string) => HTMLElementTagNameMap[K];

function appendEntry(doc: Document, body: HTMLElement, entry: ExportEntry, ids: ReadonlySet<string>, el: Make, data: ExportData, images: boolean): void {
  const section = el(body, 'section', undefined, 'entry');
  section.id = anchorId(entry.id);
  el(section, 'h2', entry.title);
  el(section, 'p', entry.typeName, 'entry-type');
  if (entry.labels.length > 0) el(section, 'p', `Labels: ${entry.labels.join(', ')}`, 'labels');
  if (entry.metadata.length > 0) {
    const table = el(section, 'table', undefined, 'metadata');
    for (const [key, value] of entry.metadata) {
      const row = el(table, 'tr');
      el(row, 'th', key);
      el(row, 'td', value);
    }
  }

  const content = el(section, 'div', undefined, 'content');
  if (entry.locked) {
    el(el(content, 'p'), 'i', '🔒 Content is locked.');
  } else {
    const fragment = doc.importNode(sanitizeEntryHtmlToFragment(entry.contentHtml), true);
    for (const link of fragment.querySelectorAll('a[href]')) {
      const target = entryIdOfLink(link.getAttribute('href') ?? '');
      if (target === null) continue;
      if (ids.has(target)) link.setAttribute('href', `#${anchorId(target)}`);
      else link.removeAttribute('href');
    }
    content.append(fragment);
  }

  if (entry.links.length > 0) {
    el(section, 'h3', 'Relationships');
    const list = el(section, 'ul', undefined, 'relationships');
    for (const link of entry.links) {
      const item = el(list, 'li');
      item.append(`${link.relationshipType}: `);
      if (link.targetExists) el(item, 'a', link.targetTitle).href = `#${anchorId(link.targetEntryId)}`;
      else item.append(link.targetTitle);
    }
  }

  const onMaps = mapsShowing(data.atlas, entry.id, true);
  if (onMaps.length > 0) {
    el(section, 'h3', 'Appears on');
    const maps = el(section, 'ul');
    for (const map of onMaps) el(el(maps, 'li'), 'a', `🗺 ${map.map.name}`).href = `#${mapAnchor(map)}`;
  }

  const pictures = entry.locked || !images ? [] : entry.images.flatMap((image) => {
    const type = imageTypeOf(image.data);
    return type ? [{ image, type }] : [];
  });
  if (pictures.length > 0) {
    el(section, 'h3', 'Images');
    const gallery = el(section, 'div', undefined, 'gallery');
    for (const { image, type } of pictures) {
      const img = el(gallery, 'img');
      img.src = `data:${type};base64,${base64(image.data)}`;
      img.alt = image.fileName;
    }
  }
}
